import type React from 'react';

import type { DefaultCaseDetailsPageProps } from './DefaultCaseDetailsPage';
import { KnipperCaseDetailsPage } from './KnipperCaseDetailsPage';

/**
 * Component type for case details pages
 * All case details pages must accept these props
 */
export type CaseDetailsPageComponent = React.FC<DefaultCaseDetailsPageProps>;

/**
 * Workflow page registry
 * Maps workflow types to their custom case details page components
 *
 * Usage:
 * 1. Import your custom page component
 * 2. Add mapping: 'workflow_type': YourCustomPageComponent
 * 3. The router will automatically use this component for that workflow type
 *
 * Example:
 * ```typescript
 * import { PBMCaseDetailsPage } from './PBMCaseDetailsPage';
 *
 * const WORKFLOW_PAGES: Record<string, CaseDetailsPageComponent> = {
 *   'pbm': PBMCaseDetailsPage,
 *   'medbv': MedBVCaseDetailsPage,
 * };
 * ```
 */
const WORKFLOW_PAGES: Record<string, CaseDetailsPageComponent> = {
  // PQC workflow - Knipper case details page
  pqc: KnipperCaseDetailsPage,

  // Add your custom workflow pages here
  // Example:
  // 'pbm': PBMCaseDetailsPage,
  // 'medbv': MedBVCaseDetailsPage,
  // 'benefit_verification': CustomBVCaseDetailsPage,
};

/**
 * Get the case details page component for a workflow type
 * Returns undefined if no custom page is registered (will fall back to default)
 *
 * @param workflowType - The workflow type to look up
 * @returns The custom page component or undefined
 */
export function getPageForWorkflow(
  workflowType: string | undefined,
): CaseDetailsPageComponent | undefined {
  if (!workflowType) {
    return undefined;
  }
  return WORKFLOW_PAGES[workflowType];
}
